'use client'

import { useEffect, useRef, useState } from 'react'
import { useParams, useRouter } from 'next/navigation'
import { useListingDetailQuery, useUserProfileLazyQuery } from 'graphql/generated/graphql'
import { gql, useMutation } from '@apollo/client'

const TRACK_EVENT = gql`mutation TrackEvent($input: TrackEventInput!) { trackEvent(input: $input) }`
import { RootState } from 'features/store'
import { useSelector } from 'react-redux'
import { Spinner } from 'flowbite-react'
import GeneralProfile from 'app/(publicScreens)/profile/GeneralProfile'
import NotFound from '@/components/NotFound'
import UpgradePopup from 'app/(navbarScreens)/directory/Upgrade-popup'
import { FormDataProvider } from '@/hook/useFormData'
import { usePersistBootstrapped } from '@/hook/usePersistBootstrapped'

const PROFILE_TABS = ['products', 'locations', 'feed', 'recipes'] as const

function tabFromSearch() {
  if (typeof window === 'undefined') return null
  const tab = new URLSearchParams(window.location.search).get('tab')
  return tab && PROFILE_TABS.includes(tab as (typeof PROFILE_TABS)[number]) ? tab : null
}

interface ProducerScreenClientProps {
  // Sanitized, server-fetched seed data for the first paint — see
  // lib/publicListingSanitizer.ts. Never the raw query response.
  initialListing: any
  initialUserDetail: any
}

const ProducerScreenClient = ({ initialListing, initialUserDetail }: ProducerScreenClientProps) => {
  const params = useParams()
  const router = useRouter()
  const slug = params?.slug as string

  const user = useSelector((state: RootState) => state?.culineerAuth.user)
  const persistBootstrapped = usePersistBootstrapped()
  const [selectedTab, setSelectedTab] = useState(tabFromSearch() || (initialListing ? 'products' : ''))
  const [userDetail, setUserDetail] = useState<Record<string, any> | null>(initialUserDetail)

  const [getUserDetail, { loading: userDetailLoader }] = useUserProfileLazyQuery({ fetchPolicy: 'no-cache', ssr: false })
  const [trackEvent] = useMutation(TRACK_EVENT)
  const trackedListingIdRef = useRef<string | null>(null)

  const { data: listingData, loading, error, refetch: refetchListing } = useListingDetailQuery({
    fetchPolicy: 'no-cache',
    variables: {
      slug
    },
    skip: !slug,
    // Critical: this query over-fetches PII (email, phone) and Stripe
    // subscription data that must never leave the server unsanitized.
    // ssr: false keeps it from running during the server render pass at
    // all — Next.js/Apollo's SSR integration otherwise embeds its full,
    // raw result into the hydration payload regardless of what the
    // sanitized server-fetched initialListing prop actually renders.
    // It still runs normally client-side, right after hydration, exactly
    // as before this change.
    ssr: false,
  })

  // Fall back to the server-fetched (sanitized) listing until the client's
  // own live query resolves, so the first paint has real content instead of
  // a blank/spinner state. Everything after hydration behaves exactly as
  // before this change — this only affects what's available before the
  // client query finishes.
  const listing = listingData ?? (initialListing ? { listingDetail: initialListing } : undefined)

  // Default the tab from listing data so the page renders for visitors
  // even when the user profile fetch hasn't completed yet.
  useEffect(() => {
    const tab = tabFromSearch()
    if (tab) {
      setSelectedTab(tab)
      return
    }
    if (listing?.listingDetail?.user_type && !selectedTab) {
      setSelectedTab('products')
    }
  }, [listing, selectedTab])

  useEffect(() => {
    if (!listing?.listingDetail) return

    const canonicalSlug = listing.listingDetail.slug
    if (canonicalSlug && canonicalSlug !== slug) {
      router.replace(`/producer/${canonicalSlug}`)
      return
    }

    if (listing.listingDetail.user?.slug) {
      getUserDetails()
    }

    if (listing.listingDetail.listing_id && persistBootstrapped) {
      const listingIdStr = String(listing.listingDetail.listing_id)
      // Guards against re-firing on every re-render: `listing` gets a new
      // object reference each time (fetchPolicy: 'no-cache'), so without
      // this the effect below re-sends the same tracking mutation dozens of
      // times per page view.
      if (trackedListingIdRef.current !== listingIdStr) {
        trackedListingIdRef.current = listingIdStr
        const viewerId = user?.user_id
        const ownerId = listing.listingDetail.user_id
        const isOwnListing = !!viewerId && !!ownerId && viewerId === ownerId
        if (!isOwnListing) {
          trackEvent({ variables: { input: {
            event_type: 'profile_viewed',
            entity_type: 'listing',
            entity_id: listingIdStr,
            source: 'producer',
          }}}).catch(() => {})
        }
      }
    }

    // Logged-out visitors are no longer hit with an "Is this yours?" popup over the page: every listing
    // here is already registered, and visitors are prompted to join as members on click instead.
  }, [slug, listing, persistBootstrapped, user?.user_id])

  const getUserDetails = async () => {
    try {
      const userRes = (await getUserDetail({
        variables: {
          slug: listing?.listingDetail?.user?.slug
        },
        errorPolicy: 'all',
      })).data
      setUserDetail(userRes ?? null)
    } catch {
      // User profile unavailable — public listing data still renders
    }
  }

  useEffect(() => {
    if (!userDetail?.userProfile) return
    const tab = tabFromSearch()
    if (tab) {
      setSelectedTab(tab)
      return
    }
    const userType = userDetail.userProfile?.default_listing?.user_type?.toLowerCase()
    switch (userType) {
      case 'producer':
        setSelectedTab('products')
        break
      case 'market':
        setSelectedTab('vendors')
        break
      default:
        setSelectedTab('recipes')
    }
  }, [userDetail, router])

  if ((loading && !initialListing) || !slug || userDetailLoader) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner aria-label="loading" size="xl" color={'success'} />
      </div>
    )
  }

  if (!listing?.listingDetail || listing.listingDetail.user_type?.toLowerCase() !== 'producer') {
    return <NotFound />
  }

  if (!listing.listingDetail.user_id) {
    if (user?.user_id) return <NotFound />
    return (
      <FormDataProvider>
        <UpgradePopup
          isOpen={true}
          setIsOpen={(open) => { if (!open) router.back() }}
          title={listing.listingDetail.name || ''}
          location={`${listing.listingDetail.city || ''}, ${listing.listingDetail.state || ''}`}
          listing={listing.listingDetail}
          isEnhanced={false}
        />
      </FormDataProvider>
    )
  }

  return (
    <>
      <GeneralProfile
        userDetail={userDetail}
        listing={{
          listing, loading: loading && !initialListing, error
        }}
        currentUser={user?.user_id}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        refetchListing={refetchListing}
      />
    </>
  )
}

export default ProducerScreenClient
